import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Building2,
  Check,
  CheckCircle2,
  Hammer,
  MailCheck,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { SectionHeading } from "@/components/section-heading";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  FORM_TEXT_MAX_LENGTH,
  FORM_TEXT_MIN_LENGTH,
  FormTextCounter,
} from "@/components/form-text-counter";
import { useLanguage } from "@/lib/i18n";
import { useServerFn } from "@tanstack/react-start";
import { submitForm } from "@/lib/forms.functions";
import { getAuthStatus } from "@/lib/auth.functions";
import { isSafeInternalPath, type AuthStatus } from "@/lib/auth.types";
import { uploadFormImages } from "@/lib/uploads.functions";
import { filesToPayload } from "@/lib/file-upload";
import { emailMessages } from "@/lib/email/messages";
import type { ProviderType } from "@/lib/provider-types";

export const Route = createFileRoute("/registrieren")({
  staticData: { sitemap: true, toasts: true },
  validateSearch: (search: Record<string, unknown>): { typ?: ProviderType; next?: string } => ({
    typ:
      search.typ === "auftragnehmer" || search.typ === "immobilienmakler"
        ? (search.typ as ProviderType)
        : undefined,
    next:
      typeof search.next === "string" && isSafeInternalPath(search.next) ? search.next : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Als Anbieter registrieren. Begner" },
      {
        name: "description",
        content:
          "Auftragnehmer und Immobilienmakler können ihr Unternehmen zur Prüfung bei Begner registrieren.",
      },
    ],
  }),
  component: RegistrationPage,
});

type RegistrationForm = {
  providerType?: ProviderType;
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  postalCode: string;
  city: string;
  website: string;
  uid: string;
  category: string;
  services: string[];
  offerTypes: string[];
  propertyTypes: string[];
  regions: string;
  description: string;
  reviewProfile: string;
  logoUrl: string;
  accuracyAccepted: boolean;
  termsAccepted: boolean;
  websiteConfirmation: string;
};

const initialForm = (providerType?: ProviderType): RegistrationForm => ({
  providerType,
  companyName: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  postalCode: "",
  city: "",
  website: "",
  uid: "",
  category: "",
  services: [],
  offerTypes: [],
  propertyTypes: [],
  regions: "",
  description: "",
  reviewProfile: "",
  logoUrl: "",
  accuracyAccepted: false,
  termsAccepted: false,
  websiteConfirmation: "",
});

const registrationCopy = {
  de: {
    eyebrow: "Anbieter werden",
    title: "In wenigen Schritten zu deinem Begner-Profil",
    intro:
      "Trage dein Unternehmen selbst ein. Wir prüfen jede Anmeldung persönlich, bevor ein Profil veröffentlicht wird.",
    requiredHint: "* Pflichtfelder",
    stepLabel: "Schritt",
    steps: ["Anbieterart", "Unternehmen", "Angebot", "Prüfung"],
    contractor: "Bau-Auftragnehmer",
    contractorHint: "Für Handwerks-, Bau- und Dienstleistungsbetriebe",
    agent: "Immobilienmakler",
    agentHint: "Für Maklerbüros und selbstständige Immobilienvermittler",
    selectType: "Wie möchtest du dich registrieren?",
    selectTypeHint: "Wähle die passende Anbieterart. Du kannst sie später noch ändern.",
    companyTitle: "Unternehmen und Kontakt",
    companyHint: "Diese Angaben helfen uns, dein Unternehmen eindeutig zu prüfen.",
    companyName: "Firmenname",
    firstName: "Vorname",
    lastName: "Nachname",
    email: "Geschäftliche E-Mail-Adresse",
    phone: "Telefonnummer",
    postalCode: "PLZ",
    city: "Ort",
    website: "Website (optional)",
    uid: "UID oder Handelsregister-Nr. (optional)",
    offerTitle: "Leistungen und Region",
    contractorOfferHint: "Wähle eine Hauptkategorie und bis zu fünf Leistungen.",
    agentOfferHint: "Zeige, welche Immobilien du in welchen Regionen vermittelst.",
    category: "Hauptkategorie",
    categoryPlaceholder: "Kategorie auswählen",
    services: "Leistungen (maximal 5)",
    offerTypes: "Vermittlung",
    propertyTypes: "Immobilienarten",
    regions: "Tätigkeitsregionen",
    contractorRegionsPlaceholder: "z. B. Basel-Stadt, Basel-Landschaft und Aargau",
    agentRegionsPlaceholder: "z. B. Nordwestschweiz und Region Zürich",
    description: "Kurzbeschreibung",
    descriptionPlaceholder:
      "Was zeichnet dein Unternehmen aus? Beschreibe Erfahrung, Arbeitsweise und Schwerpunkte.",
    chars: "Zeichen",
    descriptionMinimum: "mindestens 40 Zeichen",
    reviewTitle: "Angaben für die Prüfung",
    reviewHint:
      "Optionale Nachweise beschleunigen die Prüfung. Dein Profil wird nicht automatisch veröffentlicht.",
    reviewProfile: "Link zu Bewertungen oder Firmenprofil (optional)",
    logoUrl: "Link zum Firmenlogo (optional)",
    accuracy:
      "Ich bestätige, dass ich das Unternehmen vertreten darf und alle Angaben korrekt sind.",
    terms: "Ich akzeptiere die AGB und die Datenschutzerklärung von Begner.",
    pendingTitle: "Persönlich geprüft",
    pendingText:
      "Nach dem Absenden kontrollieren wir Kontaktangaben, Firmenauftritt und vorhandene Nachweise.",
    noPublish: "Keine automatische Veröffentlichung",
    pricingLink: "Mitgliedschaften & Preise vorab ansehen",
    back: "Zurück",
    next: "Weiter",
    submit: "Registrierung zur Prüfung senden",
    requiredError: "Bitte fülle alle Pflichtfelder in diesem Schritt aus.",
    selectionError: "Bitte wähle mindestens eine passende Option aus.",
    emailError: "Bitte gib eine gültige E-Mail-Adresse ein.",
    descriptionError: "Bitte beschreibe dein Unternehmen mit mindestens 40 Zeichen.",
    consentError: "Bitte bestätige beide Erklärungen.",
    saveError: "Die Registrierung konnte nicht gespeichert werden. Bitte versuche es erneut.",
    successToast: "Registrierung wurde zur Prüfung eingereicht.",
    successEyebrow: "Anmeldung erhalten",
    successTitle: "Danke – wir prüfen dein Unternehmen.",
    successText:
      "Deine Registrierung ist noch nicht öffentlich. Nach der persönlichen Prüfung melden wir uns über deine angegebene E-Mail-Adresse.",
    reference: "Referenz",
    status: "Status: Prüfung ausstehend",
    discoverContractors: "Auftragnehmer ansehen",
    discoverProperties: "Immobilien ansehen",
    accountCta: "Zum Firmenkonto",
    createAccountCta: "Firmenkonto erstellen",
    accountHint:
      "Prüfstatus und Pro-Zahlungsinfo finden Sie in Ihrem Firmenkonto. Die Frist für einen Auftrag läuft während der Prüfung weiter.",
    returnToJob: "Zurück zum Auftrag",
    securityTitle: "So schützen wir die Plattform",
    securityItems: [
      "Jede Registrierung wird manuell geprüft",
      "Profile erscheinen erst nach der Freigabe",
      "Geprüfte Anbieter werden klar gekennzeichnet",
    ],
    categories: [
      ["planung", "Planung, Architektur & Bauleitung"],
      ["bau", "Bauunternehmen & Renovation"],
      ["dach", "Dach, Spengler & Fassade"],
      ["fenster", "Fenster, Türen & Sonnenschutz"],
      ["sanitaer", "Sanitär, Heizung & Klima"],
      ["elektrik", "Elektrik & Smart Home"],
      ["maler", "Malerei, Fassade & Trockenbau"],
      ["putz", "Verputz & Unterlagsböden"],
      ["boden", "Bodenbeläge & Platten"],
      ["metall", "Metallbau & Schlosserei"],
      ["schreinerei", "Schreinerei & Möbel nach Mass"],
      ["garten", "Garten- & Landschaftsbau"],
      ["reinigung", "Reinigung & Hauswartung"],
      ["raeumung", "Räumung, Rückbau & Entsorgung"],
      ["umzug", "Umzug & Transport"],
      ["energie", "Photovoltaik & Energie"],
      ["abdichtung", "Abdichtung & Dämmung"],
      ["kueche", "Küchenbau & Küchenrenovation"],
      ["sicherheit", "Sicherheit & Schliesssysteme"],
      ["geruest", "Gerüstbau & Höhenarbeiten"],
      ["pool", "Pool, Sauna & Wellness"],
      ["sonstiges", "Weitere Dienstleistungen"],
    ],
    serviceOptions: {
      planung: [
        "Architektur",
        "Bauleitung",
        "Bauberatung",
        "Projektplanung",
        "Immobilienbewertung",
      ],
      bau: ["Umbau", "Renovation", "Bauleitung", "Maurerarbeiten", "Generalunternehmen"],
      dach: ["Dachdecker", "Spengler", "Flachdach", "Dachsanierung", "Dachrinnen"],
      fenster: ["Fenster", "Haustüren", "Fenstermontage", "Rollladen", "Sonnenstoren"],
      reinigung: [
        "Umzugsreinigung",
        "Baureinigung",
        "Unterhaltsreinigung",
        "Fensterreinigung",
        "Hauswartung",
      ],
      raeumung: ["Räumung", "Entsorgung", "Rückbau", "Entkernung", "Abbrucharbeiten"],
      sanitaer: ["Sanitär", "Heizung", "Wärmepumpen", "Rohrreparatur", "Badsanierung"],
      elektrik: ["Elektroinstallation", "Beleuchtung", "Photovoltaik", "Smart Home", "Service"],
      maler: ["Malerarbeiten", "Fassaden", "Trockenbau", "Verputz", "Tapezieren"],
      putz: ["Innenputz", "Aussenputz", "Fliessestrich", "Unterlagsboden", "Lehmputz"],
      boden: ["Parkett", "Platten", "Laminat", "Vinylboden", "Teppichboden"],
      metall: ["Schlosserei", "Metallbau", "Geländer", "Metalltreppen", "Zäune & Tore"],
      schreinerei: ["Möbel nach Mass", "Einbauschränke", "Türen", "Holztreppen", "Möbelmontage"],
      garten: ["Gartenpflege", "Gartengestaltung", "Pflasterung", "Baumarbeiten", "Terrassen"],
      umzug: ["Privatumzug", "Firmenumzug", "Möbelmontage", "Transport", "Einlagerung"],
      energie: ["Photovoltaik", "Solaranlagen", "Energieberatung", "Ladestationen", "Speicher"],
      abdichtung: [
        "Kellerabdichtung",
        "Flachdachabdichtung",
        "Wärmedämmung",
        "Dachbodendämmung",
        "Mauertrocknung",
      ],
      kueche: [
        "Küchen nach Mass",
        "Küchenmontage",
        "Küchenrenovation",
        "Arbeitsplatten",
        "Geräteaustausch",
      ],
      sicherheit: [
        "Schlüsseldienst",
        "Alarmanlagen",
        "Videoüberwachung",
        "Sicherheitstüren",
        "Zutrittskontrolle",
      ],
      geruest: ["Fassadengerüste", "Gerüstvermietung", "Höhenarbeiten", "Hebebühnen", "Montage"],
      pool: ["Poolbau", "Poolsanierung", "Saunabau", "Whirlpool", "Poolservice"],
      sonstiges: [
        "Handwerker-Service",
        "Reparaturen",
        "Montagearbeiten",
        "Winterdienst",
        "Beratung",
      ],
    },
    agentOfferOptions: ["Verkauf", "Vermietung", "Bewertung"],
    agentPropertyOptions: ["Wohnungen", "Häuser", "Gewerbe", "Grundstücke"],
  },
  fr: {
    eyebrow: "Devenir prestataire",
    title: "Votre profil Begner en quelques étapes",
    intro:
      "Inscrivez vous-même votre entreprise. Nous vérifions personnellement chaque demande avant sa publication.",
    requiredHint: "* Champs obligatoires",
    stepLabel: "Étape",
    steps: ["Type", "Entreprise", "Offre", "Vérification"],
    contractor: "Prestataire de construction",
    contractorHint: "Pour les entreprises artisanales, de construction et de services",
    agent: "Agent immobilier",
    agentHint: "Pour les agences et courtiers immobiliers indépendants",
    selectType: "Comment souhaitez-vous vous inscrire ?",
    selectTypeHint: "Choisissez le type de prestataire approprié.",
    companyTitle: "Entreprise et contact",
    companyHint: "Ces informations nous permettent de vérifier clairement votre entreprise.",
    companyName: "Nom de l’entreprise",
    firstName: "Prénom",
    lastName: "Nom",
    email: "E-mail professionnel",
    phone: "Téléphone",
    postalCode: "NPA",
    city: "Localité",
    website: "Site web (facultatif)",
    uid: "IDE ou registre du commerce (facultatif)",
    offerTitle: "Prestations et région",
    contractorOfferHint: "Choisissez une catégorie principale et jusqu’à cinq prestations.",
    agentOfferHint: "Indiquez les biens et les régions dans lesquels vous êtes actif.",
    category: "Catégorie principale",
    categoryPlaceholder: "Choisir une catégorie",
    services: "Prestations (maximum 5)",
    offerTypes: "Services",
    propertyTypes: "Types de biens",
    regions: "Régions d’activité",
    contractorRegionsPlaceholder: "p. ex. Bâle-Ville, Bâle-Campagne et Argovie",
    agentRegionsPlaceholder: "p. ex. Suisse du Nord-Ouest et région de Zurich",
    description: "Brève description",
    descriptionPlaceholder:
      "Décrivez votre expérience, votre méthode de travail et vos spécialités.",
    chars: "caractères",
    descriptionMinimum: "au moins 40 caractères",
    reviewTitle: "Informations de vérification",
    reviewHint:
      "Les justificatifs facultatifs accélèrent la vérification. Votre profil n’est pas publié automatiquement.",
    reviewProfile: "Lien vers des avis ou un profil d’entreprise (facultatif)",
    logoUrl: "Lien vers le logo de l’entreprise (facultatif)",
    accuracy: "Je confirme pouvoir représenter l’entreprise et que les informations sont exactes.",
    terms: "J’accepte les CG et la déclaration de confidentialité de Begner.",
    pendingTitle: "Vérification personnelle",
    pendingText: "Nous contrôlons les coordonnées, la présence en ligne et les justificatifs.",
    noPublish: "Aucune publication automatique",
    pricingLink: "Voir les abonnements et les prix",
    back: "Retour",
    next: "Suivant",
    submit: "Envoyer l’inscription pour vérification",
    requiredError: "Veuillez remplir tous les champs obligatoires de cette étape.",
    selectionError: "Veuillez sélectionner au moins une option appropriée.",
    emailError: "Veuillez saisir une adresse e-mail valide.",
    descriptionError: "Veuillez décrire votre entreprise en au moins 40 caractères.",
    consentError: "Veuillez confirmer les deux déclarations.",
    saveError: "L’inscription n’a pas pu être enregistrée. Veuillez réessayer.",
    successToast: "L’inscription a été envoyée pour vérification.",
    successEyebrow: "Demande reçue",
    successTitle: "Merci – nous vérifions votre entreprise.",
    successText:
      "Votre inscription n’est pas encore publique. Nous vous contacterons par e-mail après la vérification.",
    reference: "Référence",
    status: "Statut : vérification en attente",
    discoverContractors: "Voir les prestataires",
    discoverProperties: "Voir les biens",
    accountCta: "Ouvrir mon compte entreprise",
    createAccountCta: "Créer un compte entreprise",
    accountHint:
      "Le statut de vérification et les informations de paiement Pro se trouvent dans votre compte. La période de candidature continue pendant la vérification.",
    returnToJob: "Retour au mandat",
    securityTitle: "Comment nous protégeons la plateforme",
    securityItems: [
      "Chaque inscription est vérifiée manuellement",
      "Les profils apparaissent uniquement après validation",
      "Les prestataires vérifiés sont clairement identifiés",
    ],
    categories: [
      ["planung", "Planification, architecture et direction"],
      ["bau", "Construction et rénovation"],
      ["dach", "Toiture, ferblanterie et façade"],
      ["fenster", "Fenêtres, portes et protections solaires"],
      ["sanitaer", "Sanitaire, chauffage et climatisation"],
      ["elektrik", "Électricité et Smart Home"],
      ["maler", "Peinture, façade et cloisons sèches"],
      ["putz", "Crépi et chapes"],
      ["boden", "Sols et carrelage"],
      ["metall", "Construction métallique et serrurerie"],
      ["schreinerei", "Menuiserie et meubles sur mesure"],
      ["garten", "Jardinage et aménagement paysager"],
      ["reinigung", "Nettoyage et conciergerie"],
      ["raeumung", "Débarras, déconstruction et évacuation"],
      ["umzug", "Déménagement et transport"],
      ["energie", "Photovoltaïque et énergie"],
      ["abdichtung", "Étanchéité et isolation"],
      ["kueche", "Cuisines et rénovation de cuisines"],
      ["sicherheit", "Sécurité et systèmes de fermeture"],
      ["geruest", "Échafaudages et travaux en hauteur"],
      ["pool", "Piscine, sauna et bien-être"],
      ["sonstiges", "Autres prestations"],
    ],
    serviceOptions: {
      planung: [
        "Architecture",
        "Direction de travaux",
        "Conseil en construction",
        "Planification de projet",
        "Estimation immobilière",
      ],
      bau: [
        "Transformation",
        "Rénovation",
        "Direction de travaux",
        "Maçonnerie",
        "Entreprise générale",
      ],
      dach: ["Couverture", "Ferblanterie", "Toit plat", "Rénovation de toiture", "Gouttières"],
      fenster: ["Fenêtres", "Portes d’entrée", "Pose de fenêtres", "Volets", "Stores solaires"],
      reinigung: [
        "Nettoyage de déménagement",
        "Nettoyage de chantier",
        "Entretien",
        "Vitres",
        "Conciergerie",
      ],
      raeumung: ["Débarras", "Évacuation", "Déconstruction", "Curage", "Démolition"],
      sanitaer: [
        "Sanitaire",
        "Chauffage",
        "Pompes à chaleur",
        "Réparation de conduites",
        "Salle de bains",
      ],
      elektrik: ["Installation électrique", "Éclairage", "Photovoltaïque", "Smart Home", "Service"],
      maler: ["Peinture", "Façades", "Cloisons sèches", "Crépi", "Papier peint"],
      putz: ["Crépi intérieur", "Crépi extérieur", "Chape fluide", "Chape", "Enduit d’argile"],
      boden: ["Parquet", "Carrelage", "Stratifié", "Sol vinyle", "Moquette"],
      metall: [
        "Serrurerie",
        "Construction métallique",
        "Garde-corps",
        "Escaliers",
        "Clôtures et portails",
      ],
      schreinerei: [
        "Meubles sur mesure",
        "Armoires encastrées",
        "Portes",
        "Escaliers en bois",
        "Montage de meubles",
      ],
      garten: ["Entretien", "Aménagement", "Pavage", "Arbres", "Terrasses"],
      umzug: [
        "Déménagement privé",
        "Déménagement d’entreprise",
        "Montage",
        "Transport",
        "Stockage",
      ],
      energie: [
        "Photovoltaïque",
        "Installations solaires",
        "Conseil énergétique",
        "Bornes de recharge",
        "Stockage",
      ],
      abdichtung: [
        "Étanchéité de cave",
        "Étanchéité de toit plat",
        "Isolation thermique",
        "Isolation des combles",
        "Assèchement des murs",
      ],
      kueche: [
        "Cuisine sur mesure",
        "Montage de cuisine",
        "Rénovation de cuisine",
        "Plans de travail",
        "Remplacement d’appareils",
      ],
      sicherheit: [
        "Serrurier",
        "Systèmes d’alarme",
        "Vidéosurveillance",
        "Portes de sécurité",
        "Contrôle d’accès",
      ],
      geruest: ["Échafaudages", "Location", "Travaux en hauteur", "Nacelles", "Montage"],
      pool: ["Construction de piscine", "Rénovation", "Sauna", "Spa", "Entretien de piscine"],
      sonstiges: ["Service artisanal", "Réparations", "Montage", "Service hivernal", "Conseil"],
    },
    agentOfferOptions: ["Vente", "Location", "Estimation"],
    agentPropertyOptions: ["Appartements", "Maisons", "Commerces", "Terrains"],
  },
  it: {
    eyebrow: "Diventare fornitore",
    title: "Il tuo profilo Begner in pochi passi",
    intro:
      "Registra personalmente la tua azienda. Verifichiamo ogni richiesta prima della pubblicazione.",
    requiredHint: "* Campi obbligatori",
    stepLabel: "Passo",
    steps: ["Tipo", "Azienda", "Offerta", "Verifica"],
    contractor: "Impresa di costruzione",
    contractorHint: "Per imprese artigiane, edili e di servizi",
    agent: "Agente immobiliare",
    agentHint: "Per agenzie e mediatori immobiliari indipendenti",
    selectType: "Come desideri registrarti?",
    selectTypeHint: "Scegli il tipo di fornitore adatto.",
    companyTitle: "Azienda e contatto",
    companyHint: "Queste informazioni ci aiutano a verificare chiaramente la tua azienda.",
    companyName: "Nome dell’azienda",
    firstName: "Nome",
    lastName: "Cognome",
    email: "E-mail aziendale",
    phone: "Telefono",
    postalCode: "NPA",
    city: "Località",
    website: "Sito web (facoltativo)",
    uid: "IDI o registro di commercio (facoltativo)",
    offerTitle: "Servizi e regione",
    contractorOfferHint: "Scegli una categoria principale e fino a cinque servizi.",
    agentOfferHint: "Indica quali immobili tratti e in quali regioni.",
    category: "Categoria principale",
    categoryPlaceholder: "Scegli una categoria",
    services: "Servizi (massimo 5)",
    offerTypes: "Mediazione",
    propertyTypes: "Tipi di immobili",
    regions: "Regioni di attività",
    contractorRegionsPlaceholder: "ad es. Basilea Città, Basilea Campagna e Argovia",
    agentRegionsPlaceholder: "ad es. Svizzera nord-occidentale e regione di Zurigo",
    description: "Breve descrizione",
    descriptionPlaceholder: "Descrivi esperienza, metodo di lavoro e specializzazioni.",
    chars: "caratteri",
    descriptionMinimum: "almeno 40 caratteri",
    reviewTitle: "Informazioni per la verifica",
    reviewHint:
      "Le prove facoltative accelerano la verifica. Il profilo non viene pubblicato automaticamente.",
    reviewProfile: "Link a recensioni o profilo aziendale (facoltativo)",
    logoUrl: "Link al logo aziendale (facoltativo)",
    accuracy: "Confermo di poter rappresentare l’azienda e che i dati sono corretti.",
    terms: "Accetto le CG e l’informativa sulla privacy di Begner.",
    pendingTitle: "Verifica personale",
    pendingText: "Controlliamo i contatti, la presenza online e i documenti disponibili.",
    noPublish: "Nessuna pubblicazione automatica",
    pricingLink: "Vedi abbonamenti e prezzi",
    back: "Indietro",
    next: "Avanti",
    submit: "Invia la registrazione per la verifica",
    requiredError: "Compila tutti i campi obbligatori di questo passo.",
    selectionError: "Seleziona almeno un’opzione adatta.",
    emailError: "Inserisci un indirizzo e-mail valido.",
    descriptionError: "Descrivi la tua azienda con almeno 40 caratteri.",
    consentError: "Conferma entrambe le dichiarazioni.",
    saveError: "Non è stato possibile salvare la registrazione. Riprova.",
    successToast: "La registrazione è stata inviata per la verifica.",
    successEyebrow: "Richiesta ricevuta",
    successTitle: "Grazie – verifichiamo la tua azienda.",
    successText:
      "La registrazione non è ancora pubblica. Ti contatteremo via e-mail dopo la verifica.",
    reference: "Riferimento",
    status: "Stato: verifica in attesa",
    discoverContractors: "Vedi le imprese",
    discoverProperties: "Vedi gli immobili",
    accountCta: "Aprire l’account impresa",
    createAccountCta: "Creare un account impresa",
    accountHint:
      "Lo stato della verifica e i dati di pagamento Pro si trovano nell’account impresa. Il periodo di candidatura continua durante la verifica.",
    returnToJob: "Tornare all’incarico",
    securityTitle: "Come proteggiamo la piattaforma",
    securityItems: [
      "Ogni registrazione viene verificata manualmente",
      "I profili appaiono solo dopo l’approvazione",
      "I fornitori verificati sono chiaramente contrassegnati",
    ],
    categories: [
      ["planung", "Progettazione, architettura e direzione lavori"],
      ["bau", "Costruzione e ristrutturazione"],
      ["dach", "Tetti, lattoneria e facciate"],
      ["fenster", "Finestre, porte e protezione solare"],
      ["sanitaer", "Sanitari, riscaldamento e clima"],
      ["elektrik", "Elettricità e Smart Home"],
      ["maler", "Pittura, facciate e cartongesso"],
      ["putz", "Intonaci e massetti"],
      ["boden", "Pavimenti e piastrelle"],
      ["metall", "Costruzioni metalliche e carpenteria"],
      ["schreinerei", "Falegnameria e mobili su misura"],
      ["garten", "Giardinaggio e paesaggistica"],
      ["reinigung", "Pulizia e portineria"],
      ["raeumung", "Sgombero, demolizione e smaltimento"],
      ["umzug", "Trasloco e trasporto"],
      ["energie", "Fotovoltaico ed energia"],
      ["abdichtung", "Impermeabilizzazione e isolamento"],
      ["kueche", "Cucine e ristrutturazione cucine"],
      ["sicherheit", "Sicurezza e sistemi di chiusura"],
      ["geruest", "Ponteggi e lavori in quota"],
      ["pool", "Piscina, sauna e benessere"],
      ["sonstiges", "Altri servizi"],
    ],
    serviceOptions: {
      planung: [
        "Architettura",
        "Direzione lavori",
        "Consulenza edile",
        "Pianificazione progetti",
        "Valutazione immobiliare",
      ],
      bau: ["Ristrutturazione", "Rinnovo", "Direzione lavori", "Muratura", "Impresa generale"],
      dach: ["Copertura tetti", "Lattoneria", "Tetto piano", "Risanamento tetti", "Grondaie"],
      fenster: ["Finestre", "Porte d’ingresso", "Posa finestre", "Tapparelle", "Tende da sole"],
      reinigung: ["Pulizia trasloco", "Pulizia cantiere", "Manutenzione", "Finestre", "Portineria"],
      raeumung: [
        "Sgombero",
        "Smaltimento",
        "Demolizione",
        "Sventramento",
        "Lavori di abbattimento",
      ],
      sanitaer: ["Sanitari", "Riscaldamento", "Pompe di calore", "Riparazione tubi", "Bagni"],
      elektrik: ["Impianti elettrici", "Illuminazione", "Fotovoltaico", "Smart Home", "Assistenza"],
      maler: ["Pittura", "Facciate", "Cartongesso", "Intonaco", "Carta da parati"],
      putz: [
        "Intonaco interno",
        "Intonaco esterno",
        "Massetto fluido",
        "Massetto",
        "Intonaco d’argilla",
      ],
      boden: ["Parquet", "Piastrelle", "Laminato", "Pavimento vinilico", "Moquette"],
      metall: [
        "Carpenteria",
        "Costruzioni metalliche",
        "Ringhiere",
        "Scale",
        "Recinzioni e cancelli",
      ],
      schreinerei: [
        "Mobili su misura",
        "Armadi a muro",
        "Porte",
        "Scale in legno",
        "Montaggio mobili",
      ],
      garten: ["Cura del giardino", "Progettazione", "Pavimentazione", "Alberi", "Terrazze"],
      umzug: ["Trasloco privato", "Trasloco aziendale", "Montaggio", "Trasporto", "Deposito"],
      energie: [
        "Fotovoltaico",
        "Impianti solari",
        "Consulenza energetica",
        "Stazioni di ricarica",
        "Accumulatori",
      ],
      abdichtung: [
        "Impermeabilizzazione cantine",
        "Impermeabilizzazione tetti piani",
        "Isolamento termico",
        "Isolamento sottotetto",
        "Deumidificazione muri",
      ],
      kueche: [
        "Cucine su misura",
        "Montaggio cucine",
        "Ristrutturazione cucine",
        "Piani di lavoro",
        "Sostituzione elettrodomestici",
      ],
      sicherheit: [
        "Fabbro",
        "Sistemi d’allarme",
        "Videosorveglianza",
        "Porte di sicurezza",
        "Controllo accessi",
      ],
      geruest: ["Ponteggi", "Noleggio ponteggi", "Lavori in quota", "Piattaforme", "Montaggio"],
      pool: [
        "Costruzione piscine",
        "Risanamento piscine",
        "Saune",
        "Idromassaggio",
        "Servizio piscine",
      ],
      sonstiges: [
        "Servizio artigianale",
        "Riparazioni",
        "Montaggio",
        "Servizio invernale",
        "Consulenza",
      ],
    },
    agentOfferOptions: ["Vendita", "Affitto", "Valutazione"],
    agentPropertyOptions: ["Appartamenti", "Case", "Commercio", "Terreni"],
  },
  en: {
    eyebrow: "Become a provider",
    title: "Your Begner profile in just a few steps",
    intro:
      "List your company yourself. We personally review every application before a profile is published.",
    requiredHint: "* Required fields",
    stepLabel: "Step",
    steps: ["Provider type", "Company", "Services", "Review"],
    contractor: "Construction contractor",
    contractorHint: "For trade, construction and service businesses",
    agent: "Real-estate agent",
    agentHint: "For agencies and independent real-estate professionals",
    selectType: "How would you like to register?",
    selectTypeHint: "Choose the right provider type. You can change it later.",
    companyTitle: "Company and contact",
    companyHint: "These details help us verify your company clearly.",
    companyName: "Company name",
    firstName: "First name",
    lastName: "Last name",
    email: "Business email address",
    phone: "Phone number",
    postalCode: "Postcode",
    city: "Location",
    website: "Website (optional)",
    uid: "UID or commercial register number (optional)",
    offerTitle: "Services and region",
    contractorOfferHint: "Choose one main category and up to five services.",
    agentOfferHint: "Show which properties you handle and in which regions.",
    category: "Main category",
    categoryPlaceholder: "Select category",
    services: "Services (maximum 5)",
    offerTypes: "Agency services",
    propertyTypes: "Property types",
    regions: "Service regions",
    contractorRegionsPlaceholder: "e.g. Basel-Stadt, Basel-Landschaft and Aargau",
    agentRegionsPlaceholder: "e.g. north-western Switzerland and the Zurich region",
    description: "Short description",
    descriptionPlaceholder:
      "What makes your company stand out? Describe your experience, approach and specialisms.",
    chars: "characters",
    descriptionMinimum: "at least 40 characters",
    reviewTitle: "Details for review",
    reviewHint:
      "Optional evidence speeds up the review. Your profile is not published automatically.",
    reviewProfile: "Link to reviews or company profile (optional)",
    logoUrl: "Link to company logo (optional)",
    accuracy: "I confirm that I may represent the company and that all details are correct.",
    terms: "I accept Begner’s terms and privacy policy.",
    pendingTitle: "Personally reviewed",
    pendingText:
      "After submission, we check contact details, company presence and available evidence.",
    noPublish: "No automatic publication",
    pricingLink: "View memberships and pricing first",
    back: "Back",
    next: "Continue",
    submit: "Submit registration for review",
    requiredError: "Please complete all required fields in this step.",
    selectionError: "Please select at least one suitable option.",
    emailError: "Please enter a valid email address.",
    descriptionError: "Please describe your company in at least 40 characters.",
    consentError: "Please confirm both declarations.",
    saveError: "The registration could not be saved. Please try again.",
    successToast: "Registration submitted for review.",
    successEyebrow: "Application received",
    successTitle: "Thank you — we will review your company.",
    successText:
      "Your registration is not public yet. We will contact you at the email address provided after our personal review.",
    reference: "Reference",
    status: "Status: review pending",
    discoverContractors: "View contractors",
    discoverProperties: "View properties",
    accountCta: "Open company account",
    createAccountCta: "Create company account",
    accountHint:
      "Verification status and Pro payment details are in your company account. A job’s application window continues during verification.",
    returnToJob: "Return to job",
    securityTitle: "How we protect the platform",
    securityItems: [
      "Every registration is reviewed manually",
      "Profiles appear only after approval",
      "Verified providers are clearly identified",
    ],
    categories: [
      ["planung", "Planning, architecture & site management"],
      ["bau", "Construction & renovation"],
      ["dach", "Roofing, sheet metal & facades"],
      ["fenster", "Windows, doors & sun protection"],
      ["sanitaer", "Plumbing, heating & climate"],
      ["elektrik", "Electrical & smart home"],
      ["maler", "Painting, facades & drywall"],
      ["putz", "Plastering & screeds"],
      ["boden", "Flooring & tiling"],
      ["metall", "Metalwork & locksmithing"],
      ["schreinerei", "Carpentry & bespoke furniture"],
      ["garten", "Gardening & landscaping"],
      ["reinigung", "Cleaning & facility services"],
      ["raeumung", "Clearance, demolition & disposal"],
      ["umzug", "Moving & transport"],
      ["energie", "Solar & energy"],
      ["abdichtung", "Waterproofing & insulation"],
      ["kueche", "Kitchen fitting & renovation"],
      ["sicherheit", "Security & locking systems"],
      ["geruest", "Scaffolding & work at height"],
      ["pool", "Pools, saunas & wellness"],
      ["sonstiges", "Other services"],
    ],
    serviceOptions: {
      planung: [
        "Architecture",
        "Site management",
        "Construction consulting",
        "Project planning",
        "Property valuation",
      ],
      bau: ["Conversion", "Renovation", "Site management", "Masonry", "General contracting"],
      dach: ["Roofing", "Sheet metal", "Flat roofs", "Roof renovation", "Gutters"],
      fenster: ["Windows", "Entrance doors", "Window installation", "Roller shutters", "Awnings"],
      reinigung: [
        "End-of-tenancy cleaning",
        "Construction cleaning",
        "Regular cleaning",
        "Window cleaning",
        "Facility services",
      ],
      raeumung: ["Clearance", "Disposal", "Demolition", "Strip-out", "Demolition work"],
      sanitaer: ["Plumbing", "Heating", "Heat pumps", "Pipe repairs", "Bathroom renovation"],
      elektrik: ["Electrical installation", "Lighting", "Solar", "Smart home", "Maintenance"],
      maler: ["Painting", "Facades", "Drywall", "Plastering", "Wallpapering"],
      putz: ["Interior plaster", "Exterior render", "Flowing screed", "Screed", "Clay plaster"],
      boden: ["Parquet", "Tiling", "Laminate", "Vinyl flooring", "Carpet"],
      metall: ["Locksmithing", "Metalwork", "Railings", "Metal stairs", "Fences & gates"],
      schreinerei: [
        "Bespoke furniture",
        "Built-in wardrobes",
        "Doors",
        "Timber stairs",
        "Furniture assembly",
      ],
      garten: ["Garden maintenance", "Garden design", "Paving", "Tree work", "Terraces"],
      umzug: ["Home moves", "Business moves", "Furniture assembly", "Transport", "Storage"],
      energie: [
        "Solar",
        "Solar systems",
        "Energy consulting",
        "Charging stations",
        "Battery storage",
      ],
      abdichtung: [
        "Basement waterproofing",
        "Flat-roof waterproofing",
        "Thermal insulation",
        "Loft insulation",
        "Wall drying",
      ],
      kueche: [
        "Bespoke kitchens",
        "Kitchen installation",
        "Kitchen renovation",
        "Worktops",
        "Appliance replacement",
      ],
      sicherheit: [
        "Locksmith service",
        "Alarm systems",
        "Video surveillance",
        "Security doors",
        "Access control",
      ],
      geruest: [
        "Facade scaffolding",
        "Scaffold hire",
        "Work at height",
        "Lifting platforms",
        "Assembly",
      ],
      pool: [
        "Pool construction",
        "Pool renovation",
        "Sauna construction",
        "Hot tubs",
        "Pool service",
      ],
      sonstiges: [
        "Tradesperson service",
        "Repairs",
        "Installation work",
        "Winter service",
        "Consulting",
      ],
    },
    agentOfferOptions: ["Sales", "Lettings", "Valuation"],
    agentPropertyOptions: ["Apartments", "Houses", "Commercial", "Land"],
  },
} as const;

function RegistrationPage() {
  const { language } = useLanguage();
  const search = Route.useSearch();
  const copy = registrationCopy[language];
  const [form, setForm] = useState<RegistrationForm>(() => initialForm(search.typ));
  const [step, setStep] = useState(search.typ ? 2 : 1);
  const [error, setError] = useState("");
  const [reference, setReference] = useState("");
  const [sending, setSending] = useState(false);
  const [account, setAccount] = useState<AuthStatus | null>(null);
  const checkAuth = useServerFn(getAuthStatus);
  const send = useServerFn(submitForm);
  const uploadImages = useServerFn(uploadFormImages);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const messages = emailMessages[language];
  const accountUrl = `/konto${search.next ? `?next=${encodeURIComponent(search.next)}` : ""}`;

  useEffect(() => {
    let active = true;
    void checkAuth()
      .then((status) => {
        if (!active) return;
        setAccount(status);
        if (!status.authenticated || !status.profile) return;
        const profile = status.profile;
        setForm((current) => ({
          ...current,
          providerType:
            current.providerType ??
            (profile.role === "auftragnehmer" || profile.role === "immobilienmakler"
              ? profile.role
              : undefined),
          companyName: current.companyName || profile.companyName || "",
          firstName: current.firstName || profile.firstName || "",
          lastName: current.lastName || profile.lastName || "",
          phone: current.phone || profile.phone || "",
          email: status.user.email,
        }));
        if (
          !search.typ &&
          (profile.role === "auftragnehmer" || profile.role === "immobilienmakler")
        )
          setStep((current) => (current === 1 ? 2 : current));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [checkAuth, search.typ]);

  const services = form.category
    ? (copy.serviceOptions[form.category as keyof typeof copy.serviceOptions] ?? [])
    : [];

  const update = <K extends keyof RegistrationForm>(key: K, value: RegistrationForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError("");
  };

  const toggleArrayValue = (
    key: "services" | "offerTypes" | "propertyTypes",
    value: string,
    limit?: number,
  ) => {
    setForm((current) => {
      const values = current[key];
      if (values.includes(value)) {
        return { ...current, [key]: values.filter((item) => item !== value) };
      }
      if (limit && values.length >= limit) return current;
      return { ...current, [key]: [...values, value] };
    });
    setError("");
  };

  const validateStep = () => {
    if (step === 1 && !form.providerType) {
      setError(copy.requiredError);
      return false;
    }
    if (step === 2) {
      if (
        !form.companyName.trim() ||
        !form.firstName.trim() ||
        !form.lastName.trim() ||
        !form.email.trim() ||
        !form.phone.trim() ||
        !form.postalCode.trim() ||
        !form.city.trim()
      ) {
        setError(copy.requiredError);
        return false;
      }
      if (!/^\S+@\S+\.\S+$/.test(form.email)) {
        setError(copy.emailError);
        return false;
      }
    }
    if (step === 3) {
      const hasOffer =
        form.providerType === "auftragnehmer"
          ? Boolean(form.category && form.services.length)
          : Boolean(form.offerTypes.length && form.propertyTypes.length);
      if (!hasOffer) {
        setError(copy.selectionError);
        return false;
      }
      if (!form.regions.trim()) {
        setError(copy.requiredError);
        return false;
      }
      if (form.description.trim().length < 40) {
        setError(copy.descriptionError);
        return false;
      }
    }
    if (step === 4 && (!form.accuracyAccepted || !form.termsAccepted)) {
      setError(copy.consentError);
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    setStep((current) => Math.min(current + 1, 4));
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateStep() || form.websiteConfirmation) return;

    const isAgent = form.providerType === "immobilienmakler";
    const label = isAgent ? "Immobilienmakler" : "Bau-Auftragnehmer";
    const contactName = `${form.firstName} ${form.lastName}`.trim();

    setSending(true);
    try {
      let imageUrl = form.logoUrl;
      if (logoFile) {
        const uploaded = await uploadImages({ data: { files: await filesToPayload([logoFile]) } });
        imageUrl = uploaded.urls[0] ?? imageUrl;
      }

      const result = await send({
        data: {
          formType: "registrierung",
          referencePrefix: "BG",
          headline: `Neue Registrierung ${label}: ${form.companyName}`,
          internalSubject: `Neue Registrierung ${label}: ${form.companyName}`,
          customerSubject: copy.successTitle,
          locale: language,
          customerName: contactName,
          customerEmail: form.email,
          website: form.websiteConfirmation,
          entity: {
            type: isAgent ? "immobilienmakler" : "auftragnehmer",
            name: form.companyName,
            category: isAgent ? "Immobilienmakler" : form.category,
            description: form.description,
            location: [form.postalCode, form.city].filter(Boolean).join(" "),
            canton: form.regions,
            services: isAgent ? [...form.offerTypes, ...form.propertyTypes] : form.services,
            phone: form.phone,
            email: form.email,
            website: form.website,
            uid: form.uid,
            review_profile: form.reviewProfile,
            image_url: imageUrl,
          },
          sections: [
            {
              title: "Unternehmen",
              fields: [
                { label: "Anbietertyp", value: label },
                { label: "Firma", value: form.companyName },
                { label: "UID", value: form.uid },
                { label: "Webseite", value: form.website },
                { label: "PLZ", value: form.postalCode },
                { label: "Ort", value: form.city },
              ].filter((field) => field.value),
            },
            {
              title: "Angebot",
              fields: [
                { label: "Kategorie", value: form.category },
                { label: "Leistungen", value: form.services.join(", ") },
                { label: "Angebotsarten", value: form.offerTypes.join(", ") },
                { label: "Objekttypen", value: form.propertyTypes.join(", ") },
                { label: "Regionen", value: form.regions },
                { label: "Beschreibung", value: form.description },
                { label: "Bewertungsprofil", value: form.reviewProfile },
                { label: "Logo", value: form.logoUrl },
              ].filter((field) => field.value),
            },
            {
              title: "Kontakt",
              fields: [
                { label: "Name", value: contactName },
                { label: "E-Mail", value: form.email },
                { label: "Telefon", value: form.phone },
              ].filter((field) => field.value),
            },
          ],
        },
      });

      setReference(result.reference);
      toast.success(copy.successToast, { description: messages.confirmation });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError(messages.error);
      toast.error(messages.error);
    } finally {
      setSending(false);
    }
  };

  if (reference) {
    const destination = form.providerType === "immobilienmakler" ? "/immobilien" : "/auftragnehmer";
    return (
      <section className="bg-surface py-16 md:py-24">
        <div className="container-page">
          <div className="mx-auto max-w-2xl rounded-3xl border border-border bg-card p-7 text-center shadow-card md:p-12">
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-brand-soft text-brand-deep">
              <CheckCircle2 className="h-8 w-8" />
            </span>
            <p className="mt-6 text-sm font-semibold uppercase tracking-wider text-brand-deep">
              {copy.successEyebrow}
            </p>
            <h1 className="mt-3 font-display text-3xl font-bold text-foreground md:text-4xl">
              {copy.successTitle}
            </h1>
            <p className="mx-auto mt-4 max-w-xl leading-relaxed text-muted-foreground">
              {copy.successText}
            </p>
            <div className="mx-auto mt-7 max-w-sm rounded-2xl border border-brand/20 bg-brand-soft/55 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {copy.reference}
              </p>
              <p className="mt-1 font-display text-xl font-bold text-foreground">{reference}</p>
              <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-deep">
                <ShieldCheck className="h-4 w-4" /> {copy.status}
              </p>
            </div>
            <p className="mx-auto mt-6 max-w-xl text-sm text-muted-foreground">
              {copy.accountHint}
            </p>
            <Button asChild className="mt-4 rounded-xl shadow-brand">
              <a
                href={
                  account?.authenticated
                    ? accountUrl
                    : `/login?mode=registrieren&typ=${form.providerType ?? "auftragnehmer"}&next=${encodeURIComponent(accountUrl)}`
                }
              >
                {account?.authenticated ? copy.accountCta : copy.createAccountCta}
                <ArrowRight className="size-4" />
              </a>
            </Button>
            {search.next ? (
              <Button asChild variant="outline" className="mt-3 rounded-xl">
                <a href={search.next}>{copy.returnToJob}</a>
              </Button>
            ) : null}
            <Button asChild variant="ghost" className="mt-3 rounded-xl">
              <Link to={destination}>
                {form.providerType === "immobilienmakler"
                  ? copy.discoverProperties
                  : copy.discoverContractors}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="border-b border-border/70 bg-surface">
        <div className="container-page py-8 md:py-16">
          <SectionHeading
            eyebrow={copy.eyebrow}
            title={copy.title}
            description={copy.intro}
            level="page"
            className="max-w-3xl"
            descriptionClassName="max-w-2xl leading-relaxed"
          />
          <p className="mt-3 text-sm text-muted-foreground">{copy.requiredHint}</p>

          <div className="mt-9 grid grid-cols-4 gap-2" aria-label={`${copy.stepLabel} ${step}`}>
            {copy.steps.map((label, index) => {
              const number = index + 1;
              const complete = number < step;
              const active = number === step;
              return (
                <div key={label} className="min-w-0">
                  <div
                    className={`h-1.5 rounded-full transition-colors ${
                      number <= step ? "bg-brand" : "bg-border"
                    }`}
                  />
                  <div className="mt-2 flex items-center gap-2">
                    <span
                      className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold ${
                        active
                          ? "bg-brand text-white"
                          : complete
                            ? "bg-brand-soft text-brand-deep"
                            : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {complete ? <Check className="h-3.5 w-3.5" /> : number}
                    </span>
                    <span
                      className={`hidden truncate text-xs font-semibold sm:block ${
                        active ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-background py-8 md:py-16">
        <div className="container-page grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
          <form
            onSubmit={submit}
            className="rounded-3xl border border-border bg-card p-4 shadow-card sm:p-6 md:p-9"
          >
            {step === 1 && (
              <div>
                <h2 className="font-display text-2xl font-bold">{copy.selectType} *</h2>
                <p className="mt-2 text-muted-foreground">{copy.selectTypeHint}</p>
                <div className="mt-7 grid gap-4 sm:grid-cols-2">
                  <ProviderCard
                    active={form.providerType === "auftragnehmer"}
                    icon={Hammer}
                    title={copy.contractor}
                    description={copy.contractorHint}
                    onClick={() => update("providerType", "auftragnehmer")}
                  />
                  <ProviderCard
                    active={form.providerType === "immobilienmakler"}
                    icon={Building2}
                    title={copy.agent}
                    description={copy.agentHint}
                    onClick={() => update("providerType", "immobilienmakler")}
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="font-display text-2xl font-bold">{copy.companyTitle}</h2>
                <p className="mt-2 text-muted-foreground">{copy.companyHint}</p>
                <div className="mt-7 grid gap-5 sm:grid-cols-2">
                  <Field label={copy.companyName} required className="sm:col-span-2">
                    <Input
                      value={form.companyName}
                      onChange={(e) => update("companyName", e.target.value)}
                      required
                    />
                  </Field>
                  <Field label={copy.firstName} required>
                    <Input
                      value={form.firstName}
                      onChange={(e) => update("firstName", e.target.value)}
                      required
                    />
                  </Field>
                  <Field label={copy.lastName} required>
                    <Input
                      value={form.lastName}
                      onChange={(e) => update("lastName", e.target.value)}
                      required
                    />
                  </Field>
                  <Field label={copy.email} required>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      readOnly={Boolean(account?.authenticated)}
                      required
                    />
                  </Field>
                  <Field label={copy.phone} required>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      required
                    />
                  </Field>
                  <Field label={copy.postalCode} required>
                    <Input
                      inputMode="numeric"
                      value={form.postalCode}
                      onChange={(e) => update("postalCode", e.target.value)}
                      required
                    />
                  </Field>
                  <Field label={copy.city} required>
                    <Input
                      value={form.city}
                      onChange={(e) => update("city", e.target.value)}
                      required
                    />
                  </Field>
                  <Field label={copy.website}>
                    <Input
                      type="url"
                      placeholder="https://"
                      value={form.website}
                      onChange={(e) => update("website", e.target.value)}
                    />
                  </Field>
                  <Field label={copy.uid}>
                    <Input value={form.uid} onChange={(e) => update("uid", e.target.value)} />
                  </Field>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="font-display text-2xl font-bold">{copy.offerTitle}</h2>
                <p className="mt-2 text-muted-foreground">
                  {form.providerType === "auftragnehmer"
                    ? copy.contractorOfferHint
                    : copy.agentOfferHint}
                </p>

                {form.providerType === "auftragnehmer" ? (
                  <div className="mt-7 space-y-6">
                    <Field label={copy.category} required>
                      <Select
                        value={form.category}
                        onValueChange={(value) => {
                          setForm((current) => ({ ...current, category: value, services: [] }));
                          setError("");
                        }}
                      >
                        <SelectTrigger className="h-11 rounded-xl">
                          <SelectValue placeholder={copy.categoryPlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {copy.categories.map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    {services.length > 0 && (
                      <ChoiceGrid
                        label={`${copy.services} · ${form.services.length}/5`}
                        required
                        options={services}
                        selected={form.services}
                        onToggle={(value) => toggleArrayValue("services", value, 5)}
                      />
                    )}
                  </div>
                ) : (
                  <div className="mt-7 grid gap-6 sm:grid-cols-2">
                    <ChoiceGrid
                      label={copy.offerTypes}
                      required
                      options={copy.agentOfferOptions}
                      selected={form.offerTypes}
                      onToggle={(value) => toggleArrayValue("offerTypes", value)}
                    />
                    <ChoiceGrid
                      label={copy.propertyTypes}
                      required
                      options={copy.agentPropertyOptions}
                      selected={form.propertyTypes}
                      onToggle={(value) => toggleArrayValue("propertyTypes", value)}
                    />
                  </div>
                )}

                <div className="mt-7 space-y-6">
                  <Field label={copy.regions} required>
                    <Input
                      value={form.regions}
                      onChange={(e) => update("regions", e.target.value)}
                      placeholder={
                        form.providerType === "auftragnehmer"
                          ? copy.contractorRegionsPlaceholder
                          : copy.agentRegionsPlaceholder
                      }
                      required
                    />
                  </Field>
                  <Field label={copy.description} required>
                    <Textarea
                      value={form.description}
                      onChange={(e) => update("description", e.target.value)}
                      placeholder={copy.descriptionPlaceholder}
                      rows={5}
                      minLength={FORM_TEXT_MIN_LENGTH}
                      maxLength={FORM_TEXT_MAX_LENGTH}
                      required
                    />
                    <FormTextCounter length={form.description.length} language={language} />
                  </Field>
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <h2 className="font-display text-2xl font-bold">{copy.reviewTitle}</h2>
                <p className="mt-2 text-muted-foreground">{copy.reviewHint}</p>
                <div className="mt-7 grid gap-5 sm:grid-cols-2">
                  <Field label={copy.reviewProfile}>
                    <Input
                      type="url"
                      placeholder="https://"
                      value={form.reviewProfile}
                      onChange={(e) => update("reviewProfile", e.target.value)}
                    />
                  </Field>
                  <Field label={copy.logoUrl}>
                    <div className="space-y-2">
                      <Input
                        type="url"
                        placeholder="https://"
                        value={form.logoUrl}
                        onChange={(e) => update("logoUrl", e.target.value)}
                      />
                      <Input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
                      />
                    </div>
                  </Field>
                </div>

                <div className="mt-7 space-y-4 rounded-2xl border border-border bg-surface p-5">
                  <CheckRow
                    id="registration-accuracy"
                    checked={form.accuracyAccepted}
                    label={copy.accuracy}
                    required
                    onChange={(value) => update("accuracyAccepted", value)}
                  />
                  <CheckRow
                    id="registration-terms"
                    checked={form.termsAccepted}
                    label={copy.terms}
                    required
                    onChange={(value) => update("termsAccepted", value)}
                  />
                </div>

                <div className="absolute -left-[9999px]" aria-hidden="true">
                  <Label htmlFor="website-confirmation">Website confirmation</Label>
                  <Input
                    id="website-confirmation"
                    name="website-confirmation"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.websiteConfirmation}
                    onChange={(e) => update("websiteConfirmation", e.target.value)}
                  />
                </div>
              </div>
            )}

            {error && (
              <Alert variant="destructive" className="mt-7" role="alert">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="mt-8 grid grid-cols-2 gap-3 border-t border-border pt-6">
              {step > 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setStep((current) => Math.max(current - 1, 1));
                    setError("");
                  }}
                >
                  <ArrowLeft className="h-4 w-4" /> {copy.back}
                </Button>
              ) : (
                <span />
              )}
              {step < 4 ? (
                <Button type="button" className="w-full rounded-xl shadow-brand" onClick={goNext}>
                  {copy.next} <ArrowRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button type="submit" disabled={sending} className="w-full rounded-xl shadow-brand">
                  <ShieldCheck className="h-4 w-4" /> {sending ? messages.sending : copy.submit}
                </Button>
              )}
            </div>
          </form>

          <aside className="rounded-3xl bg-gradient-brand p-6 text-primary-foreground shadow-brand lg:sticky lg:top-24">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white/15">
              <BadgeCheck className="h-6 w-6" />
            </span>
            <h2 className="mt-5 font-display text-xl font-bold">{copy.securityTitle}</h2>
            <div className="mt-5 space-y-4">
              {copy.securityItems.map((item) => (
                <p key={item} className="flex gap-3 text-sm leading-relaxed text-white/90">
                  <Check className="mt-0.5 h-4 w-4 shrink-0" /> {item}
                </p>
              ))}
            </div>
            <div className="mt-7 rounded-2xl border border-white/20 bg-white/10 p-4">
              <p className="flex items-center gap-2 font-semibold">
                <MailCheck className="h-5 w-5" /> {copy.pendingTitle}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-white/80">{copy.pendingText}</p>
              <p className="mt-3 text-xs font-bold uppercase tracking-wider text-white/85">
                {copy.noPublish}
              </p>
            </div>
            <Link
              to="/anbieter-preise"
              search={{
                kategorie: form.providerType === "immobilienmakler" ? "makler" : "auftragnehmer",
                next: search.next,
              }}
              className="mt-5 flex items-center justify-between gap-3 rounded-xl border border-white/25 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/20"
            >
              {copy.pricingLink}
              <ArrowRight className="h-4 w-4 shrink-0" />
            </Link>
          </aside>
        </div>
      </section>
    </>
  );
}

function ProviderCard({
  active,
  icon: Icon,
  title,
  description,
  onClick,
}: {
  active: boolean;
  icon: typeof Hammer;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-2xl border p-5 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        active
          ? "border-brand bg-brand-soft shadow-card"
          : "border-border bg-background hover:border-brand/60 hover:bg-surface"
      }`}
    >
      <span
        className={`grid h-11 w-11 place-items-center rounded-xl ${active ? "bg-brand text-white" : "bg-muted text-brand-deep"}`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="mt-4 block font-display text-lg font-bold text-foreground">{title}</span>
      <span className="mt-1.5 block text-sm leading-relaxed text-muted-foreground">
        {description}
      </span>
    </button>
  );
}

function Field({
  label,
  required = false,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <Label className="mb-2 block text-sm font-semibold">
        {label}
        {required && (
          <span className="text-destructive" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </Label>
      {children}
    </div>
  );
}

function ChoiceGrid({
  label,
  required = false,
  options,
  selected,
  onToggle,
}: {
  label: string;
  required?: boolean;
  options: readonly string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div>
      <p className="mb-3 text-sm font-semibold text-foreground">
        {label}
        {required && (
          <span className="text-destructive" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </p>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = selected.includes(option);
          return (
            <button
              key={option}
              type="button"
              aria-pressed={active}
              onClick={() => onToggle(option)}
              className={`rounded-full border px-3.5 py-2 text-sm font-medium transition ${
                active
                  ? "border-brand bg-brand-soft text-brand-deep"
                  : "border-border bg-background text-muted-foreground hover:border-brand/60 hover:text-foreground"
              }`}
            >
              {active && <Check className="mr-1.5 inline h-3.5 w-3.5" />}
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CheckRow({
  id,
  checked,
  label,
  required = false,
  onChange,
}: {
  id: string;
  checked: boolean;
  label: string;
  required?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3">
      <Checkbox id={id} checked={checked} onCheckedChange={(value) => onChange(value === true)} />
      <Label htmlFor={id} className="cursor-pointer text-sm font-normal leading-relaxed">
        {required && (
          <span className="text-destructive" aria-hidden="true">
            *{" "}
          </span>
        )}
        {label}
      </Label>
    </div>
  );
}
